import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getDashboardStats } from '../store/slices/dashboardSlice';
import trendingProductService from '../services/trendingProductService';

const TrendingProducts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [trending, setTrending] = useState([]);
  const [pageError, setPageError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const loadTrending = useCallback(async () => {
    setIsLoading(true);
    setPageError(null);
    try {
      const res = await trendingProductService.list({ includeInactive: true, limit: 200 });
      setTrending(res?.products || []);
    } catch (e) {
      setPageError(e?.response?.data?.message || e?.message || 'Failed to load trending products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(getDashboardStats());
    loadTrending();
  }, [dispatch, loadTrending]);

  const handleImageError = useCallback((productId) => {
    setImageErrors((prev) => ({ ...prev, [productId]: true }));
  }, []);

  const getImageUrl = useCallback((product) => {
    if (!product.images || !product.images[0]) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjVmNWY1Ii8+PHR5cGUgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90eXBlPjwvc3ZnPg==';
    }

    const imagePath = product.images[0];
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    const apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${apiBaseUrl}${normalizedPath}`;
  }, []);

  const handleDelete = useCallback(
    (id) => {
      if (window.confirm('Are you sure you want to delete this trending product?')) {
        trendingProductService.remove(id).then(() => loadTrending());
      }
    },
    [loadTrending]
  );

  const trendingProducts = useMemo(() => trending || [], [trending]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (pageError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {pageError}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          sx={{
            color: '#000000',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Trending Products
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/products')}
            sx={{
              borderColor: '#ff2852',
              color: '#000000',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#ff2852',
                backgroundColor: 'rgba(123, 44, 191, 0.1)',
              },
            }}
          >
            All Products
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/trending-products/new')}
            sx={{
              backgroundColor: '#ff2852',
              color: '#000000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#ff2852',
              },
            }}
          >
            Add Trending Product
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box display="flex" gap={3} mb={3}>
        <Paper
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            borderTop: '4px solid #ff2852',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Total Trending Products
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {trendingProducts.length}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            borderTop: '4px solid #4caf50',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Active Trending Products
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {trendingProducts.filter((p) => p.isActive !== false).length}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            borderTop: '4px solid #9e9e9e',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Inactive Trending Products
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="text.secondary">
            {trendingProducts.filter((p) => p.isActive === false).length}
          </Typography>
        </Paper>
      </Box>

      {trendingProducts.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No trending products yet. Click <b>Add Trending Product</b> to create your first one.
        </Alert>
      )}

      <Paper
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fafafa',
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                    width: 70,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#fafafa',
                    zIndex: 10,
                  }}
                >
                  Image
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}
                >
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                  Brand
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}
                >
                  Price
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}
                >
                  Stock
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trendingProducts.map((product) => {
                const imageUrl = imageErrors[product._id]
                  ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjVmNWY1Ii8+PHR5cGUgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90eXBlPjwvc3ZnPg=='
                  : getImageUrl(product);

                return (
                  <TableRow
                    key={product._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#fffef5',
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell sx={{ width: 70 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 1,
                          border: '1px solid #ff2852',
                          overflow: 'hidden',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={product.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                          onError={() => handleImageError(product._id)}
                          loading="lazy"
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 500 }}>{product.name}</TableCell>
                    <TableCell sx={{ color: '#666666' }}>{product.category?.name}</TableCell>
                    <TableCell sx={{ color: '#666666' }}>{product.brand}</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 600 }}>
                      PKR {product.price || product.basePrice || '0.00'}
                    </TableCell>
                    <TableCell sx={{ color: '#666666' }}>{product.stock}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.isActive ? 'Active' : 'Inactive'}
                        sx={{
                          backgroundColor: product.isActive ? '#4caf50' : '#9e9e9e',
                          color: '#ffffff',
                          fontWeight: 600,
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => navigate(`/trending-products/${product._id}/edit`)}
                        sx={{
                          color: '#ff2852',
                          '&:hover': {
                            backgroundColor: 'rgba(123, 44, 191, 0.1)',
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(product._id)}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TrendingProducts;


